import React from 'react';
import { Card } from 'react-bootstrap';
import {
  BloodworkAnalysis,
  HSI_DIABETES_A1C,
  HSI_DIABETES_GLUCOSE,
} from '../../models/BloodworkAnalysis';

// Liver / Biliary → Insights tab. Shows how the Hepatic Steatosis Index (HSI)
// was calculated for THIS test: Lee 2010's formula with the test's own ALT,
// AST and BMI substituted in, plus whichever of the two fixed adjustments
// applied. Client-facing and non-diagnostic, in the same voice as the
// viscosity card in Blood Clotting & Flow: it explains the number, it doesn't
// interpret it. The calculation itself lives in
// BloodworkAnalysis.populateHSIPanelMarker; this card only re-reads its inputs.

// Round, then drop trailing zeros so entered values read cleanly (40 not
// 40.00) while computed terms keep a fixed precision.
const trim = (n: number): string => String(Math.round(n * 100) / 100);
const fixed1 = (n: number): string => (Math.round(n * 10) / 10).toFixed(1);
const fixed2 = (n: number): string => (Math.round(n * 100) / 100).toFixed(2);

const formulaStyle: React.CSSProperties = {
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: '14px',
  lineHeight: 1.9,
  color: '#20527f',
  whiteSpace: 'nowrap',
  overflowX: 'auto',
  display: 'block',
};

const noteHeadingStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '13px',
  color: '#20527f',
  margin: '0 0 6px',
};

const noteStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#5b6675',
  lineHeight: 1.6,
  margin: '0 0 10px',
};

const sensitivityTableStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#5b6675',
  borderCollapse: 'collapse',
  margin: '0 0 10px',
};

const sensitivityCellStyle: React.CSSProperties = {
  padding: '3px 14px 3px 0',
  borderBottom: '1px solid #eef1f4',
  whiteSpace: 'nowrap',
};

// How far each input moves the index across a typical span. The ratio is
// weighted eight times, so 0.8 → 1.6 is 8 × 0.8 ≈ 6.4 points; BMI is added
// as-is, so 22 → 30 is 8 points. Kept as data so the card and the Notes
// digest can't drift apart.
const SENSITIVITY_ROWS = [
  { input: 'ALT ÷ AST ratio', span: '0.8 → 1.6', effect: 'moves the index ≈ 6.4 points' },
  { input: 'BMI', span: '22 → 30', effect: 'moves the index 8 points, one for one' },
  { input: 'Sex', span: 'female', effect: 'adds 2 points' },
  {
    input: 'Blood sugar',
    span: `glucose ≥ ${HSI_DIABETES_GLUCOSE} mg/dL or HbA1c ≥ ${HSI_DIABETES_A1C}%`,
    effect: 'adds 2 points',
  },
];

// Client-facing framing of what the number does and does not say.
// Observational and non-diagnostic per LabSmarts copy rules; the cutoffs are
// Lee 2010's and match the stored reference range (optimal 0-30, lab 0-36).
const READING_INTRO =
  'A result under 30 makes liver fat unlikely and a result over 36 makes it ' +
  'likely; 30 to 36 is undecided. The index flags whether fat is likely to be ' +
  'present, not how much there is, and it says nothing about scarring. The ' +
  'reference bar above shows the same three bands.';

const READING_OUTRO =
  'BMI and the ALT-to-AST ratio are the two levers. A muscular build or fluid ' +
  'retention raises BMI without adding any fat to the liver, and an ALT raised ' +
  'for any other reason lifts the ratio, so the index is best read alongside ' +
  'the ALT, AST and GGT bars above and against the weight history rather than ' +
  'on its own.';

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
};

export type HsiComputation = {
  alt: number;
  ast: number;
  ratio: number;
  term1: number;
  bmi: number;
  female: boolean;
  diabetes?: { marker: 'HbA1c' | 'glucose'; value: number };
  result: number;
};

export class HepaticSteatosisInsights extends React.Component<Props, {}> {
  static shouldRender(bloodworkAnalysis: BloodworkAnalysis): boolean {
    const v = bloodworkAnalysis && bloodworkAnalysis.hsi;
    return !!(v && v.hasValue());
  }

  // Shared formula computation for both the rendered card and the Notes digest.
  // Re-reads the inputs populateHSIPanelMarker used; the result is the marker's
  // own value so the card can never show a different number than the bar.
  static compute(bloodworkAnalysis: BloodworkAnalysis): HsiComputation | null {
    if (!HepaticSteatosisInsights.shouldRender(bloodworkAnalysis)) {
      return null;
    }
    const alt = bloodworkAnalysis.alt.value;
    const ast = bloodworkAnalysis.ast.value;
    const ratio = alt / ast;
    return {
      alt,
      ast,
      ratio,
      term1: 8 * ratio,
      bmi: bloodworkAnalysis.bmiForAnalysis(),
      female: !!bloodworkAnalysis.bloodwork.client?.isFemale(),
      diabetes: bloodworkAnalysis.diabetesIndicator(),
      result: bloodworkAnalysis.hsi.value,
    };
  }

  // The "+ 2 + 2" tail of the worked line, only the terms that applied.
  static adjustmentTerms(c: HsiComputation): string {
    let s = '';
    if (c.female) s += ' + 2';
    if (c.diabetes) s += ' + 2';
    return s;
  }

  // One sentence naming which adjustments applied and why, or that none did.
  static adjustmentNote(c: HsiComputation): string {
    const parts: string[] = [];
    if (c.female) parts.push('2 points for female');
    if (c.diabetes) {
      const reason =
        c.diabetes.marker === 'HbA1c'
          ? `HbA1c ${trim(c.diabetes.value)}%, at or above the formula's ${HSI_DIABETES_A1C}% threshold`
          : `fasting glucose ${trim(c.diabetes.value)} mg/dL, at or above the formula's ${HSI_DIABETES_GLUCOSE} mg/dL threshold`;
      parts.push(`2 points for blood sugar (${reason})`);
    }
    if (!parts.length) {
      return (
        'No adjustment applies to this test: male, with fasting glucose and ' +
        `HbA1c below the formula's thresholds (${HSI_DIABETES_GLUCOSE} mg/dL and ` +
        `${HSI_DIABETES_A1C}%) or not measured.`
      );
    }
    return `Adjustments applied: ${parts.join('; ')}.`;
  }

  // Plain-text version of the insight for the Notes "gather from tabs" digest,
  // so Liver / Biliary's Insights reads into Notes like other sections'.
  static digestGroup(
    bloodworkAnalysis: BloodworkAnalysis,
  ): { title: string; items: string[] } | null {
    const c = HepaticSteatosisInsights.compute(bloodworkAnalysis);
    if (!c) {
      return null;
    }
    const adj = HepaticSteatosisInsights.adjustmentTerms(c);
    // Mirror the rendered card's content (render, below) as plain-text lines so
    // the digest shows the SAME information the Insights tab shows.
    return {
      title: 'Hepatic Steatosis Index (HSI)',
      items: [
        'The Hepatic Steatosis Index is not measured directly. It is calculated ' +
          "from this test's ALT and AST and the body mass index on this test, " +
          'using the formula published by Lee and colleagues in 2010: ' +
          '8 × (ALT ÷ AST) + BMI, plus 2 if female, plus 2 if fasting glucose is ' +
          `${HSI_DIABETES_GLUCOSE} mg/dL or higher or HbA1c is ${HSI_DIABETES_A1C}% or higher`,
        `Using this test's values (ALT = ${trim(c.alt)} U/L, AST = ${trim(c.ast)} U/L, BMI = ${fixed1(c.bmi)}):`,
        `= 8 × (${trim(c.alt)} ÷ ${trim(c.ast)}) + ${fixed1(c.bmi)}${adj}`,
        `= 8 × ${fixed2(c.ratio)} + ${fixed1(c.bmi)}${adj}`,
        `= ${fixed2(c.term1)} + ${fixed1(c.bmi)}${adj}`,
        `= ${fixed1(c.result)}`,
        HepaticSteatosisInsights.adjustmentNote(c),
        'Reading the result: ' + READING_INTRO,
        ...SENSITIVITY_ROWS.map(r => `${r.input} (${r.span}) ${r.effect}`),
        READING_OUTRO,
      ],
    };
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    const c = HepaticSteatosisInsights.compute(bloodworkAnalysis);
    if (!c) {
      return null;
    }
    const { alt, ast, ratio, term1, bmi, result } = c;
    const adj = HepaticSteatosisInsights.adjustmentTerms(c);

    return (
      <Card style={{ marginBottom: 12 }}>
        <Card.Header style={{ fontWeight: 'bold' }}>
          Hepatic Steatosis Index (HSI)
        </Card.Header>
        <Card.Body>
          <p style={{ marginBottom: 12 }}>
            The Hepatic Steatosis Index is not measured directly.
            <br />
            It is calculated from this test's <strong>ALT</strong> and{' '}
            <strong>AST</strong> and the <strong>body mass index</strong> on
            this test, using the formula published by Lee and colleagues in 2010:
          </p>
          <code style={formulaStyle}>
            8 × (ALT ÷ AST) + BMI
            <br />
            + 2 if female, + 2 if fasting glucose ≥ {HSI_DIABETES_GLUCOSE} mg/dL
            or HbA1c ≥ {HSI_DIABETES_A1C}%
          </code>
          <p style={{ margin: '10px 0 6px', color: '#5b6675' }}>
            Using this test's values (ALT = {trim(alt)} U/L, AST = {trim(ast)}{' '}
            U/L, BMI = {fixed1(bmi)}):
          </p>
          <code style={formulaStyle}>
            = 8 × ({trim(alt)} ÷ {trim(ast)}) + {fixed1(bmi)}
            {adj}
            <br />
            = 8 × {fixed2(ratio)} + {fixed1(bmi)}
            {adj}
            <br />
            = {fixed2(term1)} + {fixed1(bmi)}
            {adj}
            <br />
            <strong>= {fixed1(result)}</strong>
          </code>
          <p style={{ margin: '14px 0 0', fontSize: '12px', color: '#5b6675' }}>
            {HepaticSteatosisInsights.adjustmentNote(c)}
          </p>

          <hr style={{ margin: '16px 0 12px', borderColor: '#eef1f4' }} />

          <p style={noteHeadingStyle}>Reading the result</p>
          <p style={noteStyle}>{READING_INTRO}</p>
          <table style={sensitivityTableStyle}>
            <tbody>
              {SENSITIVITY_ROWS.map(r => (
                <tr key={r.input}>
                  <td style={sensitivityCellStyle}>{r.input}</td>
                  <td style={sensitivityCellStyle}>{r.span}</td>
                  <td style={sensitivityCellStyle}>{r.effect}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ ...noteStyle, marginBottom: 0 }}>{READING_OUTRO}</p>
        </Card.Body>
      </Card>
    );
  }
}
